import { createContext, useContext } from "react";

import {
  ChatDto,
  ChatParticipantDto,
  GeneralScopeDto,
  TenantProfileDto,
} from "@/core/api/generated/v1";

import { TypedEventEmitter } from "../eventEmmiters/typedEventEmitter";
import {
  ChatExtraContext,
  ChatHelper,
  ChatPermissions,
  ChatTenantToTenantContext,
} from "../helpers/entity/chat";

export type ChatContextEventsMap = {
  // list of supported events
  scrollToMessageInputRequested: undefined;
  scrollToMessageInputAndFocusRequested: undefined;
  createTagByScopeInMessageInputRequested: {
    scope: GeneralScopeDto | null | undefined;
    isScrollToInput?: boolean;
    isFocusInput?: boolean;
  };
};

export class ChatContextValue extends TypedEventEmitter<ChatContextEventsMap> {
  private _chatPerms: Nil<ChatPermissions> = undefined;
  private _chat: Nil<ChatDto> = undefined;
  private _participants: Nil<ChatParticipantDto[]> = undefined;
  private _currentParticipant: Nil<ChatParticipantDto> = undefined;
  private _extra: Nil<ChatExtraContext> = undefined;
  private _tenantToTenant: Nil<ChatTenantToTenantContext> = undefined;

  public get chatPerms() {
    return this._chatPerms;
  }

  public get chat() {
    return this._chat;
  }

  public get participants() {
    return this._participants;
  }

  public get currentParticipant() {
    return this._currentParticipant;
  }

  public get extra() {
    return this._extra;
  }

  public get tenantToTenant() {
    return this._tenantToTenant;
  }

  public setChatData(params: {
    tenantProfile: Nil<TenantProfileDto>;
    chatPerms: Nil<ChatPermissions>;
    chat: Nil<ChatDto>;
    participants: Nil<ChatParticipantDto[]>;
    currentParticipant: Nil<ChatParticipantDto>;
    extra: Nil<ChatExtraContext>;
  }): void {
    this._chatPerms = params.chatPerms;
    this._chat = params.chat;
    this._participants = params.participants;
    this._currentParticipant = params.currentParticipant;
    this._extra = params.extra;
    this._tenantToTenant =
      params.tenantProfile && params.chat
        ? ChatHelper.getTenantToTenantContext({
            currentTenantId: params.tenantProfile.id || "",
            chat: params.chat,
            extra: params.extra?.tenantToTenant,
          })
        : undefined;
  }

  public requestScrollToMessageInput(): void {
    this.emit("scrollToMessageInputRequested", undefined);
  }

  public requestScrollToMessageInputAndFocus(): void {
    this.emit("scrollToMessageInputAndFocusRequested", undefined);
  }

  public requestCreateTagByScopeInMessageInput(
    params: ChatContextEventsMap["createTagByScopeInMessageInputRequested"],
  ): void {
    this.emit("createTagByScopeInMessageInputRequested", params);
  }
}

export type ChatContextType = ChatContextValue;

export const ChatContext = createContext<ChatContextType | null>(null);

export const useChatContext = (): ChatContextType => {
  const chatContext = useContext(ChatContext);
  if (!chatContext) {
    throw new Error("useChatContext() has to be used within <ChatContext.Provider>");
  }
  return chatContext;
};

export const useChatContextOptional = (): ChatContextType | null => {
  const chatContext = useContext(ChatContext);
  return chatContext;
};
